import { CSSProperties } from "react";
import { useMediaQuery } from "@material-ui/core";
import { createTheme, useTheme } from "@material-ui/core/styles";
import { Breakpoint } from "@material-ui/core/styles/createBreakpoints";

export const theme = createTheme({
  typography: {
    fontFamily: "Open Sans, sans-serif",
    fontWeightMedium: 600,
    h1: { fontWeight: 700, fontSize: "3.5rem" },
    h6: {
      textTransform: "none" as CSSProperties["textTransform"],
    },
  },
  palette: {
    primary: {
      main: "#2A313B",
      dark: "#333",
      contrastText: "#FFF",
    },
    secondary: {
      main: "#6FCF97",
      contrastText: "#FFF",
    },
    background: {
      default: "#FFF",
    },
    text: {
      primary: "#000",
      secondary: "#828282",
    },
  },
  custom: {
    palette: {
      black: {
        main: "#2A313B",
      },
      blue: {
        main: "#2D9CDB",
        lightest: "#EAF5FB",
        light: "#84C5EA",
        dark: "#1D75A7",
      },
      gray: {
        main: "#E1E1E3",
        light: "#FAFAFA",
        dark: "#989898",
        darker: "#545454",
      },
      green: {
        main: "#6FCF97",
        light: "#E5F4EB",
        dark: "#068f66",
      },
      purple: {
        main: "#4E3ACA",
        lightest: "#F7F6FC",
        lighter: "#BAB3EA",
        light: "#EAE8F9",
        dark: "#3D2CA5",
      },
      red: {
        main: "#EB5757",
        dark: "#C51717",
      },
      softGray: {
        main: "#7C9199",
      },
      softRed: {
        main: "#CC6161",
      },
      softWhite: {
        main: "#FAFAFA",
        light: "rgb(250, 250, 250, .2)",
        dark: "#E9E9E9",
      },
      yellow: {
        main: "#FFBB00",
        light: "#FFF9E5",
        dark: "#EEAF00",
      },
    },
    shape: {
      borderRadiusSoft: 8,
    },
  },
});

export type Theme = typeof theme;

// SOURCE: https://v4.mui.com/components/use-media-query/#migrating-from-withwidth
/**
 * Be careful using this hook. It only works because the number of
 * breakpoints in theme is static. It will break once you change the number of
 * breakpoints. See https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level
 */
export function useWidth() {
  const theme: Theme = useTheme();
  const keys: Breakpoint[] = [...theme.breakpoints.keys].reverse();
  return (
    keys.reduce((output: Breakpoint | null, key: Breakpoint) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useMediaQuery(theme.breakpoints.up(key));
      return !output && matches ? key : output;
    }, null) || "xs"
  );
}

export default theme;
